/**
 * components/hazard/InfoClassSection.js — МЭДЭЭЛЛИЙН АНГИЛАЛ · ХӨДӨЛМӨРИЙН НӨХЦӨЛ.
 *
 * Хуучин зөрчлийн бүртгэл (`[71300].Register`, 2 032 мөр) дээр эдгээр гурав
 * ҮРГЭЛЖ ХАМТ, зөрчлийн тайлбараас ӨМНӨ асуугддаг байсан:
 *      Мэдээллийн төрөл · Хөдөлмөрийн нөхцөл · Эрсдэлтэй эсэх
 * Бүртгэгч эхлээд «юуны тухай, хэр аюултай вэ» гэдгээ шийдээд дараа нь
 * дэлгэрэнгүйг бичдэг. Тиймээс энэ хэсэг «Илэрсэн зөрчлийн дэлгэрэнгүй»-н
 * ДЭЭР байрлана (`hazardModes.js` → sections).
 *
 * ⚠ Зөрчлийн ТӨРӨЛ / ЗААЛТ / ШИЙТГЭЛ нь ЭНД БИШ — тэдгээр нь зөрчлийг
 *   тодорхойлсны ДАРАА тавигддаг тул `ViolationClassSection`-д, тайлбарын
 *   доор үлдэнэ.
 *
 * Эх сурвалж: `xab.violation_reference` (kind = info_class · haz_level).
 */
import React, { useEffect, useState } from "react";
import { Alert, Radio, Select, Spin, Tooltip } from "antd";

import { Card, SectionTitle } from "./common";
import RefLink from "./RefLink";
import { listViolationRefs } from "src/api/hazard";

/**
 * Хөдөлмөрийн нөхцөлийн ДҮРС.
 *
 * ⚠ Лавлах нь баазаас ирдэг тул `id`-аар зураглаж БОЛОХГҮЙ — өөр суулгац
 *   дээр өөр id-тай байна. Нэрээр нь таних ба танихгүй бол саарал дугуй
 *   болж уналаа (дүрсгүй үлдэхгүй).
 *
 * ⚠ Зөвхөн ЭНЭ суулгацын FontAwesome 5.11-д БАЙГАА нэрсийг хэрэглэв
 *   (`public/plugins/@fortawesome/…/all.min.css`-ээр шалгасан).
 *   `biohazard`, `skull-crossbones`, `radiation` нь ЭНД БАЙХГҮЙ —
 *   бичвэл хоосон дөрвөлжин гарна.
 */
const LEVEL_ICONS = [
  { re: /хэвийн бус/i, icon: "fa-exclamation-circle", color: "#F59E0B" },
  { re: /хэвийн/i, icon: "fa-check-circle", color: "#16A34A" },
  { re: /хорт/i, icon: "fa-skull", color: "#B45309" },
  { re: /аюулт/i, icon: "fa-exclamation-triangle", color: "#DC2626" },
  { re: /боломжгүй|тодорхойл/i, icon: "fa-question-circle", color: "#94A3B8" },
  { re: /бусад/i, icon: "fa-ellipsis-h", color: "#94A3B8" },
];

const FALLBACK = { icon: "fa-circle", color: "#94A3B8" };

/* Журмын эрсдэлийн ГУРВАН эрэмбэ (ХЭМАБМТ-02 4.3.6) — SeveritySection-той
   ижил утга (`severity` 1/2/3), өнгө нь riskHelpers-тэй нийцнэ. */
export const RISK_LEVELS = [
  { level: 1, name: "Бага", color: "#16A34A" },
  { level: 2, name: "Дунд", color: "#F59E0B" },
  { level: 3, name: "Өндөр", color: "#DC2626" },
];

/** ⚠ Дараалал чухал: «Хэвийн бус» нь «Хэвийн»-ээс ӨМНӨ шалгагдана. */
/** Хөдөлмөрийн нөхцөлийн нэр → icon/өнгө. Жагсаалт (incident/register)
 *  мөн ижил өнгөөр харуулдаг тул экспортлов. */
export function iconOf(name) {
  const n = String(name || "");
  return LEVEL_ICONS.find((x) => x.re.test(n)) || FALLBACK;
}

const label = (t) => (
  <div style={{ fontSize: 12, color: "#8A94A6", marginBottom: 4 }}>{t}</div>
);

/**
 * Props:
 *   showLevel · showRisk — «Хөдөлмөрийн нөхцөл», «Эрсдэлтэй эсэх»-ийг харуулах
 *   required             — ангиллын шошгод улаан * тавина
 *
 * ⚠ ЯАГААД САЛГАВ: энгийн ажилтны «Аюул мэдээлэх» маягтад ЗӨВХӨН
 *   мэдээллийн ангилал хэрэгтэй. Хөдөлмөрийн нөхцөл / эрсдэлтэй эсэх нь
 *   ХАБ-ын МЭРГЭЖЛИЙН дүгнэлт — талбар дээрх ажилтнаар тааварлуулбал
 *   өгөгдөл нь дүн шинжилгээнд ашиглах боломжгүй болно (severity-г яг
 *   энэ шалтгаанаар л энгийн маягтаас хассан — Register.js-ийн тайлбар).
 */
export default function InfoClassSection({
  value = {},
  onChange,
  showLevel = true,
  showRisk = true,
  required = false,
  /**
   * Толгойн «Лавлах сан» холбоос гарах эсэх.
   *
   * ⚠ АЮУЛ МЭДЭЭЛЭХ маягтад (`/hazard/register` — энгийн ажилтан)
   *   ГАРАХГҮЙ. Тэр холбоос нь ХАБЭА хэлтсийн лавлах удирдах хуудас руу
   *   хөтөлдөг — энгийн ажилтанд тэнд хийх зүйл байхгүй бөгөөд аюулаа
   *   мэдээлэх гэж орсон хүнийг өөр модуль руу гаргах нь төөрөгдүүлнэ.
   */
  showRefLink = true,
}) {
  const [infoClasses, setInfoClasses] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // ⚠ Түвшний лавлахыг ХЭРЭГЛЭХГҮЙ үед ч татсаар байвал энгийн ажилтны
    //   маягт дэмий нэг хүсэлт илгээнэ (гар утасны сүлжээнд мэдэгдэхүйц).
    Promise.all([
      listViolationRefs("info_class"),
      showLevel ? listViolationRefs("haz_level") : Promise.resolve({ items: [] }),
    ])
      .then(([a, b]) => {
        if (!alive) return;
        // ⚠ ҮСГИЙН ДАРААЛЛААР. Лавлах нь `sort_order`-оор ирдэг бөгөөд
        //   тэр нь хуучин 71300 системийн дугаарлалт — хэрэглэгчид ямар ч
        //   утгагүй. 7 сонголтоос хайхад цагаан толгойн дараалал хамгийн
        //   хурдан.
        //
        // ⚠ Кирилл үсгийг зөв эрэмбэлэхийн тулд `localeCompare`-д хэл
        //   зааж өгнө — байгалийн (byte) харьцуулалт нь Ө, Ү зэргийг
        //   буруу байрлуулна.
        setInfoClasses(
          [...(a?.items || [])].sort((x, y) =>
            String(x.name || "").localeCompare(String(y.name || ""), "mn"),
          ),
        );
        setLevels(b?.items || []);
        if (a?.unavailable || b?.unavailable) setUnavailable(true);
      })
      .catch(() => alive && setUnavailable(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [showLevel]);

  const pickLevel = (row) => {
    // Дахин дарвал сонголт цуцлагдана — Radio-д «болих» товч байдаггүй.
    const same = value.haz_level_id === row.id;
    onChange({
      haz_level_id: same ? null : row.id,
      haz_level_text: same ? null : row.name,
    });
  };

  return (
    <Card>
      <SectionTitle right={showRefLink ? <RefLink /> : null}>
        Мэдээллийн ангилал
      </SectionTitle>

      {unavailable ? (
        <Alert
          type="warning"
          showIcon
          style={{ fontSize: 12, marginBottom: 8 }}
          message="Лавлах бэлэн болоогүй"
          description="xab.violation_reference хүснэгт үүсээгүй байна. Энэ хэсгийг алгасаж болно."
        />
      ) : null}

      <Spin spinning={loading}>
        <div>
          {label(
            required ? (
              <>
                Мэдээллийн ангилал
                <b style={{ color: "#DC2626", marginLeft: 2 }}>*</b>
              </>
            ) : (
              "Мэдээллийн ангилал"
            ),
          )}
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder={
              infoClasses.length
                ? "Юуны тухай мэдээлэл вэ"
                : "Лавлахад бүртгэгдээгүй"
            }
            disabled={!infoClasses.length}
            optionFilterProp="label"
            value={value.info_class_id ?? undefined}
            onChange={(id) => {
              const row = infoClasses.find((r) => r.id === id);
              onChange({
                info_class_id: id ?? null,
                info_class_text: row ? row.name : null,
              });
            }}
            options={infoClasses.map((r) => ({ value: r.id, label: r.name }))}
          />
          {/* Хуучин бүртгэлд лавлахгүй текст үлдсэн байж болно */}
          {!value.info_class_id && value.info_class_text ? (
            <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}>
              Бүртгэгдсэн: {value.info_class_text}
            </div>
          ) : null}
        </div>

        {/* ── ХӨДӨЛМӨРИЙН НӨХЦӨЛ — жижиг дүрсээр ─────────────────────────
            Сонголт нь 6 тогтмол утга тул задгай жагсаалт нээх шаардлагагүй.
            Дүрс нь нэг харцаар ялгагдана: ногоон ✓ ↔ улаан ⚠ */}
        <div style={{ marginTop: 12, display: showLevel ? undefined : "none" }}>
          {/* ⚠ ЭНЭ НЬ «Эрсдэлийн түвшин» БИШ. Утга нь хөдөлмөрийн хуулиар
              тогтсон ХӨДӨЛМӨРИЙН НӨХЦӨЛ-ийн ангилал (Хэвийн · Хэвийн бус ·
              Хортой · Аюултай · Бусад · Тодорхойлох боломжгүй) бөгөөд
              2 024 бүртгэл түүгээр ангилагдсан байна. Журмын эрсдэлийн ГУРВАН эрэмбэ
              (Бага/Дунд/Өндөр) нь ӨӨР талбар — `severity`
              (SeveritySection). Хоёрыг нэг нэрээр нэрлэвэл хэрэглэгч
              аль нь алийг нь гэдгийг ялгахаа болино. */}
          {label("Хөдөлмөрийн нөхцөл")}
          {levels.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {levels.map((r) => {
                const ic = iconOf(r.name);
                const on = value.haz_level_id === r.id;
                return (
                  <Tooltip key={r.id} title={r.name}>
                    <button
                      type="button"
                      onClick={() => pickLevel(r)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        cursor: "pointer",
                        padding: "5px 9px",
                        borderRadius: 8,
                        fontSize: 11.5,
                        lineHeight: 1.1,
                        background: on ? ic.color + "1A" : "#fff",
                        border: `1px solid ${on ? ic.color : "#DDE1E8"}`,
                        color: on ? ic.color : "#5B6472",
                        fontWeight: on ? 700 : 500,
                      }}
                    >
                      <i
                        className={"fas " + ic.icon}
                        style={{ fontSize: 13, color: ic.color }}
                      />
                      {r.name}
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "#94A3B8" }}>
              Лавлахад бүртгэгдээгүй
              {value.haz_level_text ? ` · бүртгэгдсэн: ${value.haz_level_text}` : ""}
            </div>
          )}
        </div>

        {/* ── ЭРСДЭЛТЭЙ ЭСЭХ ────────────────────────────────────────
            ⚠ ГУРВАН төлөвтэй: тэмдэглээгүй / тийм / үгүй. «Тэмдэглээгүй»
              нь «үгүй»-ЭЭС ЯЛГААТАЙ — анхдагчаар «үгүй» болговол бүх
              бүртгэл эрсдэлгүй гэж ХУДЛАА тоологдоно. */}
        <div style={{ marginTop: 12, display: showRisk ? undefined : "none" }}>
          {label("Эрсдэлтэй эсэх")}
          <Radio.Group
            value={
              value.is_risk === null || value.is_risk === undefined
                ? undefined
                : !!value.is_risk
            }
            onChange={(e) => onChange({ is_risk: e.target.value })}
          >
            <Radio value={true}>Тийм</Radio>
            <Radio value={false}>Үгүй</Radio>
          </Radio.Group>
          {value.is_risk === null || value.is_risk === undefined ? (
            <span style={{ fontSize: 11.5, color: "#94A3B8", marginLeft: 8 }}>
              тэмдэглээгүй
            </span>
          ) : null}

          {/* ── ЭРСДЭЛИЙН ТҮВШИН — зөвхөн «эрсдэлтэй» бол ─────────────
              ХЭМАБМТ-02 4.3.6: Бага · Дунд · Өндөр. Зөрчлийн маягтад
              тусдаа хэсэг байхгүй тул энд «эрсдэлтэй» гэсний дараа л
              асууна; «үгүй» / тэмдэглээгүй бол `severity` анхдагч (2)
              хэвээр. */}
          {value.is_risk === true ? (
            <div style={{ marginTop: 8 }}>
              {label("Эрсдэлийн түвшин")}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {RISK_LEVELS.map((lv) => {
                  const on = Number(value.severity) === lv.level;
                  return (
                    <button
                      key={lv.level}
                      type="button"
                      onClick={() => onChange({ severity: lv.level })}
                      style={{
                        cursor: "pointer",
                        padding: "5px 12px",
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: on ? 700 : 500,
                        background: on ? lv.color + "1A" : "#fff",
                        border: `1px solid ${on ? lv.color : "#DDE1E8"}`,
                        color: on ? lv.color : "#5B6472",
                      }}
                    >
                      {lv.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </Spin>
    </Card>
  );
}
